import * as React from "react";
import { useEffect, useState, useRef } from "react";
import { extend } from "@syncfusion/ej2-base";
import {
  KanbanComponent,
  ColumnsDirective,
  ColumnDirective,
} from "@syncfusion/ej2-react-kanban";
import useStartupStore from "../stores/startupStore";
import fetchApi from "../components/Lib/api";

import styled from "styled-components";
import { IconArrowLeft } from "@tabler/icons-react";

const BackButton = styled.button`
  @media(min-width: 768px){
    display: none;
  }
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  color: #6c45e7;
  font-size: 12px;
  font-weight: 500;
  padding: 8px 0;
  cursor: pointer;
  margin-bottom: 12px;

  &:active {
    opacity: 0.7;
  }
`;
// Helper function to generate tags from subcategory name (if real tags not available)
const generateTags = (subName) => {
  if (!subName) return "";
  const words = subName.split(" ");
  if (words.length > 1) {
    return words.slice(0, 2).join(", ");
  }
  return subName;
};

// Transform the program data into a flat array of Kanban cards (one per subcategory)
const transformProgramData = (program) => {
  let cards = [];
  console.log("Program data received:", program); // Debug log

  if (!program || !program.categories || program.categories.length === 0) {
    console.log("No valid program data found"); // Debug log
    return cards;
  }

  program.categories.forEach((category, catIndex) => {
    // Support both subcategories and sub_categories property names
    const subCategories =
      category.subcategories || category.sub_categories || [];

    if (subCategories.length === 0) {
      console.log(`No subcategories found for category: ${category.name}`); // Debug log
    }

    subCategories.forEach((sub, subIndex) => {
      // Map status values to valid column keyFields
      // If status is missing or invalid, default to "Open"
      let mappedStatus = "Open"; // Default
      if (sub.status) {
        const status = sub.status.toLowerCase();
        if (status.includes("progress") || status === "in progress") {
          mappedStatus = "InProgress";
        } else if (status.includes("review")) {
          mappedStatus = "Review";
        } else if (
          status.includes("done") ||
          status.includes("close") ||
          status === "closed"
        ) {
          mappedStatus = "Close";
        } else if (
          status.includes("open") ||
          status === "todo" ||
          status === "to do"
        ) {
          mappedStatus = "Open";
        }
      }

      // Handle assignees - ensure it's always an array of valid user objects
      let assignees = [];
      if (sub.assignees && Array.isArray(sub.assignees)) {
        assignees = sub.assignees;
      } else if (sub.assigned_users && Array.isArray(sub.assigned_users)) {
        assignees = sub.assigned_users;
      }

      // Create a unique ID if not present
      const cardId = sub.id || `sub-${catIndex}-${subIndex}`;

      // Create the card with all required properties
      cards.push({
        Id: cardId,
        Title: sub.name || `Untitled Task ${cardId}`,
        Status: mappedStatus,
        Summary:
          Array.isArray(sub.guide?.blocks) && sub.guide.blocks.length > 0
            ? sub.guide.blocks[0]?.data?.text
            : sub.description || "",

        Assignee: assignees,
        Category: category.name || "Uncategorized",
        Tags: Array.isArray(sub.tags)
          ? sub.tags.map((tag) => tag.name).join(", ")
          : sub.tags || generateTags(sub.name),
        Priority: "Normal",
        Color: "#673AB8",
      });
    });
  });

  console.log("Transformed cards:", cards); // Debug log
  return cards;
};

const KanbanBoard = ({onBack}) => {
  const [kanbanData, setKanbanData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const kanbanRef = useRef(null);

  // Get program from store
  const { program, updateSubCategoryStatusInProgram, isSubmitted } = useStartupStore(
    (state) => ({
      program: state.program,
      updateSubCategoryStatusInProgram: state.updateSubCategoryStatusInProgram,
      isSubmitted: state.isSubmitted
    })
  );

  // Transform program data when it changes
  useEffect(() => {
    if (program) {
      const cards = transformProgramData(program);
      setKanbanData(cards);
      setIsLoading(false);
    }
  }, [program]);

  if (isLoading) {
    return <div>Loading Kanban board...</div>;
  }

  if (kanbanData.length === 0) {
    return (
      <div>
        No tasks found. Make sure your program has categories with
        subcategories.
      </div>
    );
  }

  // Status color mapping
  const statusColors = {
    Open: "#0251cc",
    InProgress: "#ea9713",
    Review: "#8e4399",
    Close: "#63ba3c",
  };

  // Card rendering function to apply styling
  const cardRendered = (args) => {
    if (!args.data || !args.element) return;

    const status = args.data.Status || "Open";
    const color = statusColors[status] || "#ccc";
    args.element.style.borderLeft = "5px solid " + color;
  };

  // Column header template
  const columnTemplate = (props) => {
    return (
      <div className="header-template-wrap">
        <div className={"header-icon e-icons " + props.keyField}></div>
        <div className="header-text">{props.headerText}</div>
      </div>
    );
  };

  const truncateText = (text, maxLength = 75) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  // Card template
  const cardTemplate = (props) => {
    if (!props) return null;

    return (
      <div className="card-template">
        <div className="e-card-header">
          <div className="e-card-header-caption">
            <div className="e-card-header-title e-tooltip-text">
              {props.Title || "Untitled"}
            </div>
          </div>
        </div>

        {props.Summary && (
          <div
            className="e-card-content"
            title={props.Summary} // This will show the full text in native browser tooltip
          >
            <div className="e-text">{truncateText(props.Summary)}</div>
          </div>
        )}

        <div className="e-card-custom-footer">
          {/* Display tags if available */}
          {props.Tags &&
            typeof props.Tags === "string" &&
            props.Tags.split(",").map((tag, index) => (
              <div className="e-card-tag-field e-tooltip-text" key={index}>
                {tag.trim()}
              </div>
            ))}

          {/* Display assignee avatars */}
          <div className="e-card-assignees">
            {Array.isArray(props.Assignee) &&
              props.Assignee.map((user, index) => (
                <div
                  className="e-card-avatar"
                  key={index}
                  title={getUserFullName(user)}
                >
                  {getUserInitials(user)}
                </div>
              ))}
          </div>
        </div>
      </div>
    );
  };

  // Helper function to get user initials
  const getUserInitials = (user) => {
    if (!user) return "";

    // Try all possible ways the user object might store first/last name
    const firstName =
      user.first_name ||
      user.firstName ||
      (user.user && user.user.first_name) ||
      "";
    const lastName =
      user.last_name ||
      user.lastName ||
      (user.user && user.user.last_name) ||
      "";

    let initials = "";
    if (firstName) initials += firstName[0].toUpperCase();
    if (lastName) initials += lastName[0].toUpperCase();

    return initials || "?";
  };

  // Helper function to get full name for tooltip
  const getUserFullName = (user) => {
    if (!user) return "Unknown";

    const firstName =
      user.first_name ||
      user.firstName ||
      (user.user && user.user.first_name) ||
      "";
    const lastName =
      user.last_name ||
      user.lastName ||
      (user.user && user.user.last_name) ||
      "";

    return [firstName, lastName].filter(Boolean).join(" ") || "Unknown User";
  };

  async function updateSubCategoryStatus(subCategoryId, newStatus) {
    try {
      const response = await fetchApi(
        `/sub_categories/${subCategoryId}/update_status`,
        "PATCH",
        { sub_category: { status: newStatus } }
      );

      if (!response.ok) {
        const errorData = await response.json();
        console.error("Error updating status:", errorData);
        return;
      }

      const data = await response.json();
      console.log("Status updated successfully:", data);
      // Optionally update your UI or application state based on data
    } catch (error) {
      console.error("Error updating status:", error.message);
    }
  }

  async function updateSubCategoryTags(subCategoryId, tagsString) {
    try {
      // Convert comma-separated string to array and trim whitespace
      const tagNames = tagsString
        ? tagsString.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0)
        : [];
      
      const response = await fetchApi(
        `/sub_categories/${subCategoryId}/update_tags`,
        "POST",
        { tag_names: tagNames }
      );
  
      if (!response.ok) {
        const errorData = await response.json();
        console.error("Error updating tags:", errorData);
        return;
      }
  
      const data = await response.json();
      
      // Update local kanban data
      setKanbanData(prevData => 
        prevData.map(card => 
          card.Id === subCategoryId 
            ? { ...card, Tags: tagsString }
            : card
        )
      );
    } catch (error) {
      console.error("Error updating tags:", error.message);
    }
  }

  const onDragStop = (args) => {
    const subCategoryId = args.data[0].Id;
    const newStatus = args.data[0].Status;
  
    updateSubCategoryStatus(subCategoryId, newStatus); // API update
  
    // Update local state via the store action
    updateSubCategoryStatusInProgram(subCategoryId, newStatus);
  };

  const onDialogClose = async (args) => {
    console.log(args);
    if (args.requestType === 'Edit') {
      const updatedCard = args.data;
      const subCategoryId = updatedCard.Id;

      // Update status if changed
      if (updatedCard.Status) {
        await updateSubCategoryStatus(subCategoryId, updatedCard.Status);
        updateSubCategoryStatusInProgram(subCategoryId, updatedCard.Status);
      }

      // Update tags if changed
      if (updatedCard.Tags !== undefined) {
        await updateSubCategoryTags(subCategoryId, updatedCard.Tags);
      }
    }
  };

  return (
    <div className="schedule-control-section">
      <BackButton onClick={onBack}>
        <IconArrowLeft size={12} />
        Back to Editor
      </BackButton>
      <div className="col-lg-12 control-section">
        <div className="control-wrapper">
          <KanbanComponent
            id="kanban"
            cssClass="kanban-overview"
            keyField="Status"
            dataSource={kanbanData}
            enableTooltip={true}
            allowDragAndDrop={!isSubmitted} // Keep general drag-and-drop enabled
            dragStop={onDragStop} // Add the drag stop handler
            swimlaneSettings={{
              keyField: "Category",
              sortComparer: (c) => c,
              allowDragAndDrop: false,
              collapsible: true,
              isCollapsed: true,
            }}
            cardSettings={{
              headerField: "Title",
              template: cardTemplate,
              selectionType: "Multiple",
            }}
            cardRendered={cardRendered}
            dialogSettings={{
              fields: [
                { text: "ID", key: "Title", type: "TextBox" },
                { text: "Column", key: "Status", type: "DropDown" },
                { text: "Tags (comma-separated)", key: "Tags", type: "TextBox" },
              ],
            }}
            dialogClose={onDialogClose}
            cardClick={(args) => {
              const isMobile = window.innerWidth <= 768;
              const kanbanInstance = kanbanRef.current;
              if (!kanbanInstance) {
                console.warn("Kanban instance not ready yet");
                return;
              }
            
              // call the Kanban API method to open edit dialog
              if (typeof kanbanInstance.openDialog === 'function') {
                // most likely: openDialog takes card data (Kanban will infer edit)
                console.log("came inside 1st ")
                kanbanInstance.openDialog('Edit', args.data);
                return;
              }
            
              // older/newer wrappers might expose ej2 instance under ej2Instances or instance
              const ej2 = kanbanInstance.ej2Instances || kanbanInstance.instance || kanbanInstance;
              if (ej2 && typeof ej2.openDialog === 'function') {
                console.log("came inside 2nd ")
                ej2.openDialog('Edit', args.data);
                return;
              }
            
              // fallback: try openEditDialog (not normally present, but harmless to check)
              if (typeof kanbanInstance.openEditDialog === 'function') {
                console.log("came inside 3rd ")
                kanbanInstance.openEditDialog('Edit', args.data);
                return;
              }
            }}
            ref={kanbanRef}
          >
            <ColumnsDirective>
              <ColumnDirective
                headerText="To Do"
                keyField="Open"
                allowToggle={true}
                template={columnTemplate}
              />
              <ColumnDirective
                headerText="In Progress"
                keyField="InProgress"
                allowToggle={true}
                template={columnTemplate}
              />
              <ColumnDirective
                headerText="In Review"
                keyField="Review"
                allowToggle={true}
                template={columnTemplate}
              />
              <ColumnDirective
                headerText="Done"
                keyField="Close"
                allowToggle={true}
                template={columnTemplate}
              />
            </ColumnsDirective>
          </KanbanComponent>
        </div>
      </div>
    </div>
  );
};

export default KanbanBoard;
